import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import { getAuth } from 'firebase/auth';
import { config } from '../config';

// Define API response interface
export interface ApiResponse<T = any> {
  data: T;
  status: number;
  statusText: string;
  headers: any;
}

// API client class
export class ApiClient {
  private static instance: AxiosInstance;
  private static refreshPromise: Promise<string> | null = null;
  private static pendingRequests: Map<string, Promise<any>> = new Map();

  // Get the singleton instance of the API client
  public static getInstance(): AxiosInstance {
    if (!this.instance) {
      const baseURL = config.apiUrl || '/api';
      
      this.instance = axios.create({
        baseURL,
        headers: {
          'Content-Type': 'application/json'
        }
        // Note: If you're experiencing issues with CORS preflight OPTIONS requests being rejected,
        // the proper solution is to configure your server to handle OPTIONS requests without authentication.
        // In ASP.NET Core, this means placing UseCors() before UseAuthentication() in your middleware pipeline.
        // 
        // Example server-side configuration:
        // app.UseCors(builder => builder
        //     .AllowAnyOrigin()
        //     .AllowAnyMethod()
        //     .AllowAnyHeader());
        // app.UseAuthentication();
        // app.UseAuthorization();
      });

      // Add request interceptor for auth token
      this.instance.interceptors.request.use(
        async (config) => {
          // Get the current Firebase user's ID token
          const token = await this.getIdToken();
          
          if (token && config.headers) {
            config.headers.Authorization = `Bearer ${token}`;
          }
          return config;
        },
        (error) => Promise.reject(error)
      );

      // Add response interceptor for handling 401s
      this.instance.interceptors.response.use(
        (response) => response,
        async (error) => {
          const originalRequest = error.config;

          if (error.response?.status === 401 && !originalRequest._retry) {
            originalRequest._retry = true;

            try {
              // Force refresh the token
              const newToken = await this.getIdToken(true);
              
              if (originalRequest.headers) {
                originalRequest.headers.Authorization = `Bearer ${newToken}`;
              }
              
              return this.instance(originalRequest);
            } catch (refreshError) {
              // If refresh fails, let the auth system handle it
              console.error('Token refresh failed:', refreshError);
              throw refreshError;
            }
          }

          return Promise.reject(error);
        }
      );
    }

    return this.instance;
  }

  // Get the current user's ID token, optionally forcing a refresh
  private static async getIdToken(forceRefresh = false): Promise<string> {
    const auth = getAuth();
    const currentUser = auth.currentUser;
    
    if (!currentUser) {
      return '';
    }

    // If a refresh is already in progress, wait for it
    if (this.refreshPromise) {
      return this.refreshPromise;
    }

    try {
      // Start a new refresh
      this.refreshPromise = currentUser.getIdToken(forceRefresh);
      return await this.refreshPromise;
    } finally {
      this.refreshPromise = null;
    }
  }

  // Generate a request key for deduplication
  private static getRequestKey(method: string, url: string, data?: any): string {
    return `${method}:${url}:${data ? JSON.stringify(data) : ''}`;
  }

  // Helper methods for common HTTP methods with deduplication
  public static async get<T = any>(url: string, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    const requestKey = this.getRequestKey('GET', url);
    
    if (this.pendingRequests.has(requestKey)) {
      return this.pendingRequests.get(requestKey) as Promise<ApiResponse<T>>;
    }
    
    const requestPromise = this.getInstance().get<T>(url, config)
      .then(response => {
        this.pendingRequests.delete(requestKey);
        return this.processResponse(response);
      })
      .catch(error => {
        this.pendingRequests.delete(requestKey);
        throw error;
      });
    
    this.pendingRequests.set(requestKey, requestPromise);
    return requestPromise;
  }

  public static async post<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    // For POST requests, we typically don't want to deduplicate as they're meant to create new resources
    const response = await this.getInstance().post<T>(url, data, config);
    return this.processResponse(response);
  }

  public static async put<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    // For PUT requests, we typically don't want to deduplicate as they're meant to update resources
    const response = await this.getInstance().put<T>(url, data, config);
    return this.processResponse(response);
  }

  public static async delete<T = any>(url: string, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    // For DELETE requests, we typically don't want to deduplicate as they're meant to delete resources
    const response = await this.getInstance().delete<T>(url, config);
    return this.processResponse(response);
  }

  // Process the response to standardize the return format
  private static processResponse<T>(response: AxiosResponse<T>): ApiResponse<T> {
    return {
      data: response.data,
      status: response.status,
      statusText: response.statusText,
      headers: response.headers
    };
  }
} 